import { EventEmitter, Listener } from "src/util/event-emitter";
import { growText } from "src/fx/object/sprite-fx";
import BaseScene from "src/scenes/base";
import { nanoid } from "nanoid";
import { resolvePercentX, resolvePercentY } from "src/util/util";
import TextFadePopup from "src/game-objects/text-fade-popup";
import { sample } from "lodash";

interface RadialColorAlignerOpts {
    direction?: 'left' | 'right' | 'random',
    speed?: number,
    x?: number | string,
    y?: number | string,
    radius?: number,
    strokeWidth?: number
}

interface GenerateRadialTextureOpts {
    colors: chroma.Color[][],
    speed: number,
    radius: number,
    strokeWidth: number,
    textureKey: string,
    x: number,
    y: number
}

interface RadialSlice {
    tier: 0 | 1 | 2,
    color: chroma.Color,
    sprite: Phaser.GameObjects.Sprite
}

// interface RadialCollider {
//     tier: number,
//     path: Phaser.Geom.Circle,
//     color: Phaser.Display.Color,
//     sprites: Phaser.GameObjects.Sprite
// }

interface RadialTier {
    sliceSize: number,
    active: boolean,
    tier: number,
    speed: number,
    path: Phaser.Geom.Circle,
    sprite: Phaser.GameObjects.Sprite,
    fxSprite: Phaser.GameObjects.Sprite,
    colliders: Phaser.GameObjects.Sprite[]
}

function multiplyArray<T>(arr: T[], multiplier: number): T[] {
    const result: T[] = [];
    
    arr.forEach(element => {
      for (let i = 0; i < multiplier; i++) {
        result.push(element);
      }
    });
    
    return result;
}
  
/**
 * Generates a sprite which shows the overlap between a slice of two tiers. 
 * NOTE: Ended up not using since this only really makes sense when the player knows which color they
 * are locking in.
 * 
 * @param scene 
 * @param radius 
 * @param tier1 
 * @param tier2 
 * @returns 
 */
function generateOverlapSlice(scene: Phaser.Scene, radius: number, tier1: RadialTier, tier2: RadialTier){
    /**
     * Just fumbled around until these calculations worked. No idea the logic behind it.
     */
    let x = scene.cameras.main.width / 2;
    let y = scene.cameras.main.height / 2;
    let radiusClamped = Phaser.Math.Clamp(radius, 0, scene.cameras.main.width / 2);
    let minRad = Math.min(tier1.sprite.rotation, tier2.sprite.rotation);
    let maxRad = Math.max(tier1.sprite.rotation, tier2.sprite.rotation);
    let sliceSizeRad = Phaser.Math.DegToRad(tier1.sliceSize);
    let textureKey = nanoid();
    let g = scene.add.graphics();
        g.lineStyle(40, 0xffffff, 1);
        g.beginPath();
        g.arc(x, y, radiusClamped, minRad + sliceSizeRad, maxRad, true);
        g.strokePath();
        g.generateTexture(textureKey);
        g.destroy();

    return scene.add.sprite(x, y, textureKey);
}

function generateRadialSpriteGroup(scene: Phaser.Scene, opts: GenerateRadialTextureOpts) {
    let x = scene.cameras.main.width / 2;
    let y = scene.cameras.main.height / 2;
    let radius = Phaser.Math.Clamp(opts.radius, 0, scene.cameras.main.width / 2);

    /**
     * Generate a texture for a single slice of the radial group. This texture will be used for all the sprites.
     * Generating a new texture for each one is way too resource-heavy to do.
     */
    let g = scene.add.graphics();
        g.visible = false;

    let g2 = scene.add.graphics();
        g2.visible = false;

    let tiers : RadialTier[] = [];
    for (let i = 0; i < opts.colors.length; i++){ //loop through 3d array of colors (i.e. tiers)
        
        let colors = opts.colors[i];
        if (colors.length % 4 !== 0 || colors.length > 16){
            throw new Error('generateRadialSpriteGroup(): Color array length must be divisible by 4 and have a total of at most 16 elements.')
        } 

        let angleDivisions = 360 / colors.length;
        let tierRadius = radius - ((i * 4) + (i * opts.strokeWidth)); //Adding the extra `i` here to allow for a pixel of padding between each ring
        let path = new Phaser.Geom.Circle(x, y, tierRadius);
        let colliders : Phaser.GameObjects.Sprite[] = []

        // let colliderMultiplier = 20 / colors.length; //Used to generate more colliders if our color segments are going to be large
        // let colorsFilled = multiplyArray(colors, colliderMultiplier); //Expand colors array to account for excess colliders

        for (let j = 0; j < colors.length; j++){ //loop through one of the arrays of colors 
            let angleStart = Phaser.Math.DegToRad(j * (angleDivisions));
            let angleEnd = Phaser.Math.DegToRad((j + 1) * (angleDivisions));
    
            //let tint = j === 0 ? colors[j].num() : 0xffffff00;

            if (j % 2 !== 0) continue;

            g.lineStyle(opts.strokeWidth, colors[j].num(), 1);
            g.beginPath();
            g.arc(x, y, tierRadius, angleStart, angleEnd, false, .01);
            g.strokePath();

            // let sprite = scene.add.sprite(0, 0, '_');
            //     sprite.scale = tierRadius / radius;
            //     //sprite.setDisplayOrigin(.5, .5);
            //     sprite.alpha = .1;
            //     //sprite.visible = false;
            //     sprite.setInteractive();
            //     sprite.input.alwaysEnabled = true; //Ensures sprite recieves input when invisible
            //     sprite.setData({
            //         tier: i,
            //         color: colorsFilled[j]
            //     })

            //     sprite.setDepth(100);

            
            // colliders.push(sprite);
        }

        let halfAngleDivision = angleDivisions / 2;
        Phaser.Actions.PlaceOnCircle(colliders, path, Phaser.Math.DegToRad(halfAngleDivision), Phaser.Math.DegToRad(360 + halfAngleDivision));
        //Phaser.Actions.PlaceOnCircle(colliders, path, Phaser.Math.DegToRad(0), Phaser.Math.DegToRad(360));

        let textureKey = `radialtier-${nanoid()}`;
        g.generateTexture(textureKey);

        let sprite = scene.add.sprite(x, y, textureKey);
            sprite.angle = Phaser.Math.Between(0, 360); //Assign a random angle


        //Generate the plain white arc used for placement effect
        let fxTextureKey = nanoid();
        g2.lineStyle(opts.strokeWidth, 0xffffff, 1);
        g2.beginPath();
        g2.arc(x, y, tierRadius, Phaser.Math.DegToRad(0), Phaser.Math.DegToRad(360), false);
        g2.strokePath();
        g2.generateTexture(fxTextureKey);
        let fxSprite = scene.add.sprite(x, y, fxTextureKey);
            fxSprite.alpha = 0;

        tiers.push({
            sliceSize: angleDivisions,
            active: true,
            tier: i,
            path,
            sprite: sprite,
            fxSprite: fxSprite,
            colliders: colliders,
            speed: opts.speed + (.1 * i)
        })

        g.clear();
        g2.clear();
    }

    g.destroy();
    g2.destroy();
    return tiers;
}

class RadialColorAligner<T extends BaseScene> {
    
    private events: EventEmitter = new EventEmitter();
    private scene: BaseScene;
    private tiers: RadialTier[] = [];
    private opts: Required<RadialColorAlignerOpts>;
    private centerX: number = 0;
    private centerY: number = 0;
    private selected: Phaser.GameObjects.Sprite | null = null;
    private readonly maxStepRatio: number = .003;
    //private stepDirectionMultiplier: number;
    private directionMultipliers: number[] = [];
    private stepRatio: number;
    private donut: Phaser.GameObjects.Sprite;
    private text: ReturnType<typeof growText>;
    private tierLockIndex: number = 0;
    private baseRequiredAccuracy: number = .75;
    private locked: boolean = false;

    
    constructor(scene: T, colors: chroma.Color[][], opts: RadialColorAlignerOpts = {}){
        this.scene = scene;
        this.opts = {
            speed: .5,
            direction: 'left',
            x: '50%',
            y: '50%',
            radius: (scene.cameras.main.width / 2) * .9,
            strokeWidth: 40,
            ...opts
        };

        let textureKey = `radialtexture-${nanoid()}`;
        this.centerX = typeof this.opts.x === 'string' ? resolvePercentX(scene, this.opts.x) : this.opts.x;
        this.centerY = typeof this.opts.y === 'string' ? resolvePercentY(scene, this.opts.y) : this.opts.y;
        this.tiers = generateRadialSpriteGroup(scene, {
            colors: colors,
            speed: this.opts.speed,
            radius: this.opts.radius,
            strokeWidth: this.opts.strokeWidth,
            textureKey: textureKey,
            x: this.centerX,
            y: this.centerY
        })

        this.donut = scene.add.sprite(this.centerX, this.centerY, 'donut');
        this.donut.alpha = 0;
        this.donut.setDepth(200);
        // this.textPopup = new TextFadePopup(scene, { x: this.centerX, y: this.centerY });
        // this.textPopup.setFontFamily('Montserrat');
        // this.textPopup.setColor('#ffffff');
        // this.textPopup.setOrigin(.5, .5);

        this.text = growText(this.scene, '', { 
            //shimmer: true,
            x: this.centerX, 
            y: this.centerY,
            enterDuration: 250,
            exitDuration: 150,
            style: {
                fontFamily: 'Montserrat', 
                fontSize: '18px', 
            }
        })

        //this.stepDirectionMultiplier = this.opts.direction === 'left' ? -1 : 1; //Step sign to determine direction
        colors.forEach((c, i) => {
            let multiplier = 1;
            if (this.opts.direction === 'left') multiplier = -1;
            else if (this.opts.direction === 'random') multiplier = sample([1, -1])!;
            this.directionMultipliers.push(multiplier);
        })

        this.stepRatio = this.opts.speed * this.maxStepRatio; //Our actual step ratio is a percentage of our max percentage
        this.scene.registerUpdate(this);
    }

    private getTierSpeed(tier: number, delta: number){
        this.stepRatio = this.tiers[tier].speed * this.maxStepRatio; //Our actual step ratio is a percentage of our max percentage
        let deltaStep = (this.directionMultipliers[tier] ?? 1) * (this.stepRatio * delta);
        return deltaStep;
    }

    public update(time: number, delta: number){
        this.tiers.forEach((tier, i) => {
            if (tier.active){
                tier.sprite.rotation += this.getTierSpeed(i, delta);
            }
        })
    }

    public async lockTier() {
        if (this.locked) return;
            this.locked = true;

        let tier = this.tiers[this.tierLockIndex];
        let tierParent = this.tiers[this.tierLockIndex - 1];
        if (tier){
            tier.active = false;

            let isFinalTier = this.tierLockIndex === this.tiers.length - 1;
            let accuracyLeniency = Phaser.Math.Clamp(this.tierLockIndex - 1, 0, Infinity) * .075;
            let requiredCurrentAccuracy = .8 - accuracyLeniency, 
                requiredAvgAccuracy = .65,
                currentAccuracy = tierParent ? this.getRotationAccuracyForTiers(tier, tierParent) : 1, 
                currentAvgAccuracy = this.getRotationAccuracy().average;

            /**
             * Is final tier, check total average accuracy
             */
            if (isFinalTier){
                /**
                 * For final test, we check both the current accuracy and the average accuracy. If the user performed
                 * poorly on the final tier, but performed well on the previous tiers, we will still pass them.
                 */
                if (currentAccuracy < requiredCurrentAccuracy && currentAvgAccuracy < requiredAvgAccuracy){
                    this.failTier(currentAvgAccuracy);
                    return;
                }

                this.events.emit('complete');
                this.tiers.forEach(t => {
                    t.active = true;
                    t.speed = 3;
                })

                this.events.emit('lock');
                await this.flashTier(this.tierLockIndex, currentAccuracy);
                this.events.emit('success', currentAvgAccuracy);
                return;

            /**
             * Is not final tier, check accuracy of current tier and ensure it is not so terrible
             * that it just ends the game.
             */
            } else if (currentAccuracy < requiredCurrentAccuracy){
                this.failTier(currentAccuracy);
                return;
            }
            
            this.events.emit('lock');
            this.flashTier(this.tierLockIndex, currentAccuracy, this.tierLockIndex === 0);
            this.tierLockIndex += 1;
        }

        this.locked = false;
    }

    private failTier(accuracy: number){
        this.flashTier(this.tierLockIndex, accuracy);
        this.events.emit('fail');
    }

    flashTier(index: number, accuracy: number, flashOnly: boolean = false){
        let tier = this.tiers[index];
        if (tier){
            let { fxSprite } = this.tiers[index];
            fxSprite.alpha = .5;
            fxSprite.scale = 1;
            fxSprite.setDepth(0);

            let rating = 'Perfect!';
            let tint = '#0f0';
            if (accuracy <= .75){
                rating = 'Bad!';
                tint = '#FF776F';
            } else if (accuracy <= .8){
                rating = 'Poor!';
                tint = '#FED543';
            } else if (accuracy < .95){
                rating = 'Good!';
                tint = '#0f0';
            }

            return Promise.all([
                this.text.enter({
                    text: rating,
                    shimmer: accuracy >= .95,
                    style: {
                        color: accuracy >= .95 ? '#ffffff' : tint
                    }
                }),
                new Promise((res) => {
                    this.scene.tweens.add({
                        ease: Phaser.Math.Easing.Quartic.Out,
                        targets: fxSprite,
                        alpha: 0,
                        scale: 2,
                        duration: 1000,
                        onComplete: () => {
                            this.text.exit();
                            res(null);
                        }
                    })
                })
            ])
        }
    }

    public getRotationAccuracyForTiers(tier1: RadialTier, tier2: RadialTier){
        let min = Phaser.Math.RadToDeg(Math.min(tier1.sprite.rotation, tier2.sprite.rotation));
        let max = Phaser.Math.RadToDeg(Math.max(tier1.sprite.rotation, tier2.sprite.rotation));
        let split = Phaser.Math.Clamp(1 - ((max - min) / tier1.sliceSize), 0, 1);
        return split;
    }

    /**
     * Returns two values, the average difference of rotation for all the disks as a percentage value and
     * the difference between the two disks with the greatest separation (also as a percentage). The average
     * can be used to determine how well the player performed while the min/max can tell you whether they
     * managed to get overlap on all disks. A value of zero here would indicate that they did not.
     * 
     */
    public getRotationAccuracy(){
        let sliceSize = this.tiers[0].sliceSize; //Currently assuming all slice sizes are the same
        let rotations = this.tiers.map(t => t.sprite.rotation);
        // let average = Phaser.Math.Clamp(rotations.reduce((acc, rotation, i) => {
        //     if (i !== 0){
        //         let compare = [rotations[i], rotations[i - 1]];
        //         let min = Phaser.Math.RadToDeg(Math.min(...compare));
        //         let max = Phaser.Math.RadToDeg(Math.max(...compare));

        //         console.log("Accuracy for index", i, 1 - ((max - min) / sliceSize));

        //         acc += 1 - ((max - min) / sliceSize);
        //     }
        //     return acc;
        // }, 0) / rotations.length, 0 , 1);

        let average = this.tiers.reduce((acc, tier, i) => {
            if (i !== 0){
                // let compare = [rotations[i], rotations[i - 1]];
                // let min = Phaser.Math.RadToDeg(Math.min(...compare));
                // let max = Phaser.Math.RadToDeg(Math.max(...compare));

                // console.log("Accuracy for index", i, 1 - ((max - min) / sliceSize));

                acc += this.getRotationAccuracyForTiers(this.tiers[i], this.tiers[i - 1])
            }
            return acc;
        }, 0) / (this.tiers.length - 1);

        let min = Phaser.Math.RadToDeg(Math.min(...rotations));
        let max = Phaser.Math.RadToDeg(Math.max(...rotations));
        let split = Phaser.Math.Clamp(1 - ((max - min) / sliceSize), 0, 1);
        return { average, split }
    }


    public enter(delay: number = 0){
        let originalSpeed = this.opts.speed;
        this.opts.speed = 5;
        return Promise.all([
            new Promise((res, rej) => {
                this.scene.tweens.add({
                    ease: Phaser.Math.Easing.Quartic.In,
                    targets: this.opts,
                    speed: originalSpeed,
                    duration: 1000, 
                    delay: delay,
                    onComplete: () => res(null)
                });
            }),
            new Promise((res, rej) => {
                this.tiers.forEach(t => t.sprite.alpha = 0);
                this.scene.tweens.add({
                    ease: Phaser.Math.Easing.Linear,
                    targets: this.tiers.map(t => t.sprite),
                    alpha: 1,
                    duration: 1000, 
                    delay: delay,
                    onComplete: () => res(null)
                });
            })
        ])
    }

    public async leave(delay = 0){
        // let toScale = Phaser.Utils.Array.GetRandom([0, 3]);
        // let arr = toScale > 0 ? this.tiers : [...this.tiers].reverse();
        let toScale = 3; //NOTE: Going to scale=0 looks kind of like a toilet flushing so...I removed it for now
        let arr = this.tiers;
        let promises = arr.map((tier, i) => {
            return Promise.all([
                new Promise((res, rej) => {
                    this.scene.tweens.add({
                        ease: Phaser.Math.Easing.Quadratic.Out,
                        targets: tier.sprite,
                        scale: toScale,
                        duration: 500, 
                        delay: i * 100,
                        onComplete: () => res(null)
                    });
                }),
                new Promise((res, rej) => {
                    this.scene.tweens.add({
                        ease: Phaser.Math.Easing.Quadratic.Out,
                        targets: tier.sprite,
                        alpha: 0,
                        duration: 500, 
                        delay: i * 100,
                        onComplete: () => res(null)
                    });
                })
            ])
        });

        await Promise.all(promises);
        this.scene.unregisterUpdate(this);
    }

    public start(){
        this.scene.registerUpdate(this);
    }

    public on(event: string, listener: Listener){
        this.events.on(event, listener);
    }

    public removeListener(event: string, listener: Listener){
        this.events.removeListener(event, listener);
    }

    public destroy(){
        this.scene.unregisterUpdate(this);
        this.text.destroy();
        this.tiers.forEach(t => t.sprite.destroy());
        this.events.removeAllListeners();
    }
}

export {
    RadialColorAligner
}